import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Slide from '@mui/material/Slide';
import { useTheme } from '@mui/material/styles';

import { useBreakpoint } from '../../hooks/useBreakpoint/useBreakpoint';
import { useSlideshow } from '../../hooks/useSlideshow/useSlideshow';
import { ImageCarouselControl } from '../ImageCarouselControl/ImageCarouselControl';
import {
  ImageCarouselMediaItem,
  type ImageCarouselMediaItemProps,
} from '../ImageCarouselMediaItem/ImageCarouselMediaItem';
import { ImageCarouselSidebar } from '../ImageCarouselSidebar/ImageCarouselSidebar';
import { Typography } from '../Typography/Typography';
import { CarouselItemContainer, ImageCounterContainer, ImageCounterOverlay } from './styles';

const IMAGE_COUNTER_SEPARATOR = ' / ';

const formatImageCounter = (selectedIndex: number, imageCount: number) => `${selectedIndex + 1} / ${imageCount}`;

export interface ImageCarouselSidebarProps {
  label?: string;
  children?: React.ReactNode;
}

export interface ImageCarouselProps {
  photoEvidence: string[];
  showActions?: boolean;
  selectedMedia?: string;
  showImageCounterOverlay?: boolean;
  showImageCounter?: boolean;
  handleNavigate?: (src: string) => void;
  boxHeight?: string | number;
  useSwipeControls?: boolean;
  slotProps?: {
    mediaItem?: Omit<ImageCarouselMediaItemProps, 'media'>;
    mediaItemSlot?: React.ReactNode;
    sidebar?: ImageCarouselSidebarProps;
  };
  imageCount: number;
}

export const ImageCarousel = ({
  photoEvidence,
  showActions,
  selectedMedia,
  showImageCounterOverlay,
  showImageCounter,
  handleNavigate,
  boxHeight = '100dvh',
  useSwipeControls,
  slotProps,
  imageCount,
}: ImageCarouselProps) => {
  const theme = useTheme();
  const { isMobile } = useBreakpoint();
  const showSidebar = Boolean(!isMobile && slotProps?.sidebar);
  const showLegendTop = Boolean(isMobile && slotProps?.sidebar);

  const { currentImage, handleTouchEnd, handleTouchStart, next, previous, selectedIndex } = useSlideshow(
    selectedMedia || photoEvidence[0],
    photoEvidence,
    handleNavigate
  );

  return (
    <Grid
      container
      sx={{
        height: '100%',
      }}
    >
      <Grid
        size={{
          sm: showSidebar ? 8 : 12,
          xs: 12,
        }}
        sx={{
          height: '100%',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: boxHeight,
            alignItems: 'flex-start',
          }}
        >
          <CarouselItemContainer
            data-testid="image-carousel-swipe-container"
            onTouchStart={useSwipeControls ? handleTouchStart : undefined}
            onTouchEnd={useSwipeControls ? handleTouchEnd : undefined}
            useSwipeControls={useSwipeControls}
          >
            {slotProps?.mediaItemSlot || <ImageCarouselMediaItem media={currentImage} {...slotProps?.mediaItem} />}
          </CarouselItemContainer>

          <Box sx={{ visibility: showLegendTop ? 'visible' : 'hidden', width: '100%' }}>
            {showLegendTop ? (
              <Box
                sx={{
                  mt: 2,
                }}
              >
                {slotProps?.sidebar?.children}
              </Box>
            ) : null}
          </Box>

          {showImageCounterOverlay ? (
            <ImageCounterOverlay>
              <Typography color="white" fontWeight="bold">
                {formatImageCounter(selectedIndex, imageCount)}
              </Typography>
            </ImageCounterOverlay>
          ) : null}

          {showImageCounter ? (
            <ImageCounterContainer>
              {showActions ? (
                <ImageCarouselControl
                  data-testid="previous-photo"
                  onClick={previous}
                  direction="left"
                  disabled={selectedIndex === 0}
                />
              ) : null}
              <Typography data-testid="image-counter">
                <Typography component="span" fontWeight="bold">
                  {selectedIndex + 1}
                </Typography>
                {IMAGE_COUNTER_SEPARATOR}
                <Typography component="span" fontWeight="bold">
                  {imageCount}
                </Typography>
              </Typography>
              {showActions ? (
                <ImageCarouselControl
                  data-testid="next-photo"
                  onClick={next}
                  direction="right"
                  disabled={selectedIndex === imageCount - 1}
                />
              ) : null}
            </ImageCounterContainer>
          ) : null}
        </Box>
      </Grid>
      <Grid
        sx={{
          backgroundColor: theme.palette.background.default,
        }}
        size={{
          sm: 4,
          xs: 0,
        }}
      >
        {showSidebar ? (
          <Slide
            direction="left"
            in={showSidebar}
            mountOnEnter
            unmountOnExit
            timeout={{
              enter: 300,
              exit: 300,
            }}
          >
            <div>
              <ImageCarouselSidebar label={String(slotProps?.sidebar?.label || '')}>
                {slotProps?.sidebar?.children}
              </ImageCarouselSidebar>
            </div>
          </Slide>
        ) : null}
      </Grid>
    </Grid>
  );
};
